<script lang="ts" setup>
import { Delta } from "@vueup/vue-quill";
import { CriteriaInstance } from "types/Checklist";
import {
  EvaluationCriteria,
  EvaluationRelevantInstance,
} from "types/EvaluationResults";
import { useSystemCommentsStore } from "~/composables/useSystemCommentsStore";

const props = defineProps({
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  criteria: {
    type: Object as PropType<EvaluationCriteria>,
    required: true,
  },
  relevantInstance: {
    type: Object as PropType<EvaluationRelevantInstance>,
    required: false,
  },
  paragraphPosition: {
    type: Number,
    required: false,
  },
  criteriaIndex: {
    type: Number,
    required: true,
  },
});

const documentId = inject("documentId") as any;
const { saveEvaluationResults } = useStepResultsStore(documentId);

const emit = defineEmits(["onTogglePassed"]);
const documentChecklistStore = useDocumentChecklistStore(documentId);

const checklistCriteria = computed(() => {
  if (props.criteriaInstance.checklistCriteriaId == undefined) {
    return undefined;
  }

  return documentChecklistStore.getCriteriaFromId(
    props.criteriaInstance.checklistCriteriaId
  );
});

const selectedValues = computed(() => {
  if (props.criteriaInstance.selectedTypes == undefined) {
    return (props.criteriaInstance.selectedTypes = {});
  }

  return props.criteriaInstance.selectedTypes;
});

const computedKey = computed(() => {
  return (key: string) => {
    return props.criteriaIndex + "-" + key;
  };
});

const isValueSelected = computed(() => {
  return (key: string, value: string) => {
    return selectedValues.value[key] == value;
  };
});

const onChange = (key: string, value: string) => {
  if (selectedValues.value[key] == value) {
    delete selectedValues.value[key];
  } else {
    selectedValues.value[key] = value;
  }

  props.criteria.isOverriden = false;
  saveEvaluationResults();
};

const systemCommentsStore = useSystemCommentsStore();
const { decorationComments } = storeToRefs(systemCommentsStore);

const comments = computed(() => {
  return (type: string) => {
    return decorationComments.value(type, documentId);
  };
});

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const { setSelection } = useDocumentSelectionStore(documentId);

const openAnnotationForm = () => {
  const commentOnlyAnnotation = createAnnotation();

  setAnnotationBeingEdited(documentId, commentOnlyAnnotation);
};

const { saveAnnotation } = useSaveAnnotation(documentId);

const addAnnotation = (comment: string) => {
  const commentOnlyAnnotation = createAnnotation();

  if (!commentOnlyAnnotation) return;

  commentOnlyAnnotation.commentObject = new Delta().insert(comment).ops;
  commentOnlyAnnotation.quickComment = comment;

  saveAnnotation(
    commentOnlyAnnotation,
    useIsAnnotationCommentLong(commentOnlyAnnotation)
  ).then();
};

const documentTokenIdPositionMap =
  useDocumentTokenIdPositionMapStore(documentId);
const { tokenGlobalPositionFromId } = storeToRefs(documentTokenIdPositionMap);

const createAnnotation = () => {
  // First I need to set the selection to this relevant instances tokens
  // So first I need the start and end global positions for this relevant instance

  if (!props.relevantInstance) return;

  props.relevantInstance.tokens?.sort((a, b) => a.position - b.position);
  var startId = props.relevantInstance.tokens?.[0].id;
  var endId =
    props.relevantInstance.tokens?.[props.relevantInstance.tokens.length - 1]
      .id;
  var startGlobalPosition = tokenGlobalPositionFromId.value(startId);
  var endGlobalPosition = tokenGlobalPositionFromId.value(endId);

  setSelection(startGlobalPosition, endGlobalPosition);

  return createDefaultAnnotation();
};

const count = computed(() => {
  return (value: string) => {
    return Object.values(selectedValues.value).filter((v) => v == value).length;
  };
});
</script>

<template>
  <div v-if="checklistCriteria">
    <div
      v-for="(values, key) in checklistCriteria.overrides"
      :key="key"
      class="flex flex-col"
    >
      <span
        v-for="(value, index) in values"
        :key="index"
        class="flex flex-row justify-between items-center hover:bg-light-default cursor-pointer px-2 min-h-[30px]"
        @click="onChange(computedKey(key as string), value)"
      >
        <BasePopover
          v-if="relevantInstance"
          :delay="{ show: 100, hide: 500 }"
          :disabled="comments(value).length == 0"
          :show-trigger-methods="['hover']"
          :hide-trigger-methods="['hover']"
          align="start"
        >
          <BaseIconButton @click.stop="openAnnotationForm">
            <BaseIconCommentBox />
          </BaseIconButton>
          <template #content>
            <div class="flex flex-col p-1">
              <span
                v-for="(comment, index) in comments(value)"
                :key="index"
                @mousedown.prevent
                class="p-2 cursor-pointer hover:bg-light-default"
                @click="addAnnotation(comment.comment)"
              >
                {{ comment.comment }}
              </span>
            </div>
          </template>
        </BasePopover>
        <input
          v-if="criteria.config.isMechanics != true"
          type="checkbox"
          :checked="isValueSelected(computedKey(key as string), value)"
          @click.stop
          ref="checkboxRef"
          class="mr-2 w-[30px] criteria-checkbox pointer-events-none"
        />
        <span class="select-none grow text-body-2">
          {{ value }} <span v-if="count(value) > 0">({{ count(value) }})</span>
        </span>
        <span
          v-if="isValueSelected(computedKey(key as string), value)"
          class="flex flex-row items-center"
        >
          <BaseTextButton
            @click.stop="$emit('onTogglePassed', true)"
            color="success"
            class="h-[30px]"
            :class="{
              'bg-success-default bg-opacity-10':
                criteria.passed == true && criteria.isOverriden == true,
            }"
          >
            correct
          </BaseTextButton>
          <span class="mx-1">|</span>
          <BaseTextButton
            @click.stop="$emit('onTogglePassed', false)"
            color="danger"
            class="h-[30px]"
            :class="{
              'bg-danger-default bg-opacity-10':
                criteria.passed != true && criteria.isOverriden == true,
            }"
          >
            incorrect
          </BaseTextButton>
        </span>
      </span>
    </div>
  </div>
</template>

<style scoped></style>
